'use client';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { Menu, X, ChevronDown, Sun, Moon } from 'lucide-react';
import { useT } from '@/lib/useT';
import { LanguageSwitcher } from './LanguageSwitcher';
import PWAInstallButton from './PWAInstallButton';
import { readTheme, applyTheme, type PublicTheme } from '@/lib/publicTheme';

type NavItem = { href: string; label: string; sub?: string; anchor?: boolean };

function resolveHref(item: NavItem, isLanding: boolean): string {
  if (!item.anchor) return item.href;
  if (!isLanding) return item.href;
  const i = item.href.indexOf('#');
  return i >= 0 ? item.href.slice(i) : item.href;
}

/**
 * Public navbar — тот же язык, что у §01 homepage (`.lxh-hd`): 72px,
 * плоская светлая подложка, тонкая линия снизу, вордмарк LOGI·X·A,
 * оранжевая primary-кнопка. Отличие от homepage — выпадающие меню:
 * под-страницам нужна полная навигация, а не 4 якоря.
 */
export default function PublicHeader() {
  const pathname = usePathname();
  const t = useT();
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const isLanding = pathname === '/'
    || /^\/(en|ru|ua|pl|cz|md|bg|kz)$/.test(pathname || '');   // per-language homepages

  // Day/night. Дефолт — светлая; тёмная включается только явным нажатием
  // (см. lib/publicTheme). Здесь только чтение состояния для иконки —
  // сам атрибут `data-theme` ставит inline-скрипт в layout до отрисовки.
  const [theme, setTheme] = useState<PublicTheme>('light');
  useEffect(() => {
    const sync = () => setTheme(readTheme());
    sync();
    window.addEventListener('logixa-theme-change', sync);
    return () => window.removeEventListener('logixa-theme-change', sync);
  }, []);
  const toggleTheme = () => {
    const next: PublicTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    applyTheme(next);
  };
  const ThemeBtn = ({ className }: { className?: string }) => (
    <button
      onClick={toggleTheme}
      aria-label={theme === 'dark' ? 'Helles Design' : 'Dunkles Design'}
      title={theme === 'dark' ? 'Helles Design' : 'Dunkles Design'}
      className={`w-9 h-9 rounded flex items-center justify-center shrink-0 transition-colors ${className || ''}`}
      style={{ border: '1px solid var(--c-border2)', color: 'var(--c-muted)', background: 'transparent' }}
      onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--c-heading)'; e.currentTarget.style.borderColor = 'var(--c-heading)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--c-muted)'; e.currentTarget.style.borderColor = 'var(--c-border2)'; }}
    >
      {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
    </button>
  );

  const PLATTFORM: NavItem[] = [
    { href: '/platform/funktionen',    label: t('pub_dd_features'),     sub: t('pub_dd_features_sub')     },
    { href: '/platform/backoffice',    label: t('pub_dd_backoffice'),   sub: t('pub_dd_backoffice_sub')   },
    { href: '/platform/ki',            label: t('pub_dd_ai'),           sub: t('pub_dd_ai_sub')           },
    { href: '/platform/integrationen', label: t('pub_dd_integrations'), sub: t('pub_dd_integrations_sub') },
  ];
  const KUNDEN: NavItem[] = [
    { href: '/marketplace',         label: t('mkt_index_badge'),        sub: t('mkt_index_sub') },
    { href: '/jobs',                label: t('jobs_badge'),             sub: t('jobs_sub') },
    { href: '/kunden/marktplaetze', label: t('pub_dd_marketplaces'),    sub: t('pub_dd_marketplaces_sub') },
    { href: '/kunden/portal',       label: t('pub_dd_customer_portal'), sub: t('pub_dd_customer_portal_sub') },
  ];
  const COMPANY: NavItem[] = [
    { href: '/about',          label: t('footer_link_about'), sub: t('hdr_dd_about_sub') },
    { href: '/companies',      label: t('pub_dd_companies'), sub: t('pub_dd_companies_sub') },
    { href: '/docs',           label: t('hdr_dd_docs'),      sub: t('hdr_dd_docs_sub') },
    { href: '/blog',           label: t('blog_title'),       sub: t('hdr_dd_blog_sub') },
    { href: '/kontakt',        label: t('pub_dd_contact'),   sub: t('pub_dd_contact_sub') },
  ];
  const PRICING: NavItem = { href: '/pricing', label: t('pub_nav_pricing') };
  const MOBILE_NAV: NavItem[] = [...PLATTFORM, ...KUNDEN, PRICING, ...COMPANY];

  // Close any open dropdown on outside click
  useEffect(() => {
    if (!openMenu) return;
    const onDoc = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setOpenMenu(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [openMenu]);

  const linkStyle: React.CSSProperties = { color: 'var(--c-text)', fontSize: '14.5px', fontWeight: 500 };

  const Dropdown = ({ id, label, items }: { id: string; label: string; items: NavItem[] }) => {
    const isOpen = openMenu === id;
    return (
      <div className="relative">
        <button
          onClick={() => setOpenMenu(isOpen ? null : id)}
          className="transition-colors inline-flex items-center gap-1 whitespace-nowrap py-1"
          style={{ ...linkStyle, color: isOpen ? 'var(--c-heading)' : 'var(--c-text)' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--c-heading)')}
          onMouseLeave={(e) => { if (!isOpen) e.currentTarget.style.color = 'var(--c-text)'; }}
          aria-expanded={isOpen}
        >
          {label}
          <ChevronDown size={13} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <div
            className="absolute top-full left-0 mt-3 py-2 w-[280px]"
            style={{
              background: 'var(--c-bg)',
              border: '1px solid var(--c-border)',
              borderRadius: '8px',
              boxShadow: 'var(--shadow-lg)',
            }}
          >
            {items.map((item) => {
              const href = resolveHref(item, isLanding);
              const Body = (
                <div
                  className="block px-4 py-2.5 transition-colors"
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--accent-soft)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <p className="text-[13px] font-semibold" style={{ color: 'var(--c-heading)' }}>{item.label}</p>
                  {item.sub && <p className="text-[11px] mt-0.5" style={{ color: 'var(--c-muted)' }}>{item.sub}</p>}
                </div>
              );
              return item.anchor ? (
                <a key={item.label} href={href} onClick={() => setOpenMenu(null)}>{Body}</a>
              ) : (
                <Link key={item.label} href={item.href} onClick={() => setOpenMenu(null)}>{Body}</Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
    {/* sticky, а не fixed — как §01 homepage. Шапка занимает место в потоке,
        поэтому под-страницам не нужен ручной offset (раньше он был разный:
        pt-20 где-то, paddingTop:64 в CmsPageRenderer — и заголовки уезжали
        под шапку). */}
    <header
      className="sticky top-0 z-50"
      style={{
        background: 'var(--c-bg)',
        borderBottom: '1px solid var(--c-border)',
        height: '72px',
      }}
    >
      <div className="h-[72px] flex items-center justify-between gap-4 px-5 md:px-8 max-w-[1440px] mx-auto">
        {/* Вордмарк — тот же знак, что в §01 homepage */}
        <Link href="/" className="shrink-0" aria-label="LOGIXA">
          <span className="logixa-word">LOGI<i>X</i>A</span>
        </Link>

        {/* Desktop nav (lg+ only) */}
        <div ref={navRef} className="hidden lg:flex items-center gap-7 mx-auto">
          <Dropdown id="plattform" label={t('pub_nav_platform')} items={PLATTFORM} />
          <Dropdown id="kunden"    label={t('pub_nav_customers')} items={KUNDEN} />
          <a href={resolveHref(PRICING, isLanding)}
            className="transition-colors whitespace-nowrap py-1"
            style={linkStyle}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--c-heading)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--c-text)')}>
            {t('pub_nav_pricing')}
          </a>
          <Dropdown id="company" label={t('pub_nav_about')} items={COMPANY} />
        </div>

        {/* Actions */}
        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <ThemeBtn />
          <LanguageSwitcher compact />
          <PWAInstallButton variant="header" />
          <a href="/login"
            className="transition-colors whitespace-nowrap"
            style={linkStyle}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--c-heading)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--c-text)')}>
            {t('pub_nav_login')}
          </a>
          <a href="/register"
            className="inline-flex items-center justify-center h-[42px] px-[22px] text-[14.5px] font-semibold transition-colors"
            style={{ background: 'var(--accent)', color: 'var(--accent-ink)', borderRadius: '4px' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--accent-2)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--accent)')}>
            {t('pub_nav_start')}
          </a>
        </div>

        {/* Mobile actions: theme toggle + burger */}
        <div className="lg:hidden flex items-center gap-2 shrink-0">
          <ThemeBtn />
          <button onClick={() => setOpen((o) => !o)}
            className="w-11 h-11 rounded flex items-center justify-center"
            style={{ border: '1px solid var(--c-border2)', background: 'transparent', color: 'var(--c-heading)' }}
            aria-label="Menu" aria-expanded={open}>
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
    </header>

      {/* Mobile full-overlay — kept OUTSIDE <header> so position:fixed stays
          viewport-relative (a backdrop-filter ancestor would make a containing
          block for fixed descendants and collapse the panel to ~0 height). */}
      {open && (
        <div className="lg:hidden fixed left-0 right-0 top-[72px] bottom-0 z-40 overflow-y-auto px-5 py-4"
          style={{ background: 'var(--c-bg)', borderTop: '1px solid var(--c-border)' }}>
          {MOBILE_NAV.map((item) => {
            const href = resolveHref(item, isLanding);
            const cls = 'flex items-center min-h-[44px] text-[15px] font-medium';
            const st = { color: 'var(--c-heading)' };
            return item.anchor ? (
              <a key={item.label} href={href} onClick={() => setOpen(false)} className={cls} style={st}>{item.label}</a>
            ) : (
              <Link key={item.label} href={item.href} onClick={() => setOpen(false)} className={cls} style={st}>{item.label}</Link>
            );
          })}
          <div className="pt-4 flex justify-center">
            <LanguageSwitcher />
          </div>
          <div className="flex gap-2 pt-4">
            <a href="/login" onClick={() => setOpen(false)}
              className="flex-1 h-[46px] inline-flex items-center justify-center text-[14.5px] font-semibold"
              style={{ background: 'transparent', color: 'var(--c-heading)', border: '1px solid var(--c-border2)', borderRadius: '4px' }}>
              {t('pub_nav_login')}
            </a>
            <a href="/register" onClick={() => setOpen(false)}
              className="flex-1 h-[46px] inline-flex items-center justify-center text-[14.5px] font-semibold"
              style={{ background: 'var(--accent)', color: 'var(--accent-ink)', borderRadius: '4px' }}>
              {t('pub_nav_start')}
            </a>
          </div>
        </div>
      )}
    </>
  );
}
